import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import httpClient from "../../utils/httpClient";

type RequestType = {
  isLoading: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  error: string | null;
};

const initialState: RequestType = {
  isLoading: false,
  data: null,
  error: "",
};

const isGatewayInvalidInputError = (error: any) =>
  error?.status === 403 ||
  error?.response?.status === 403 ||
  error?.request?.status === 403 ||
  error?.code === "ERR_NETWORK" ||
  error?.message === "Network Error";

interface ApproveNewCostRequest {
  Action: string;
  SkuId: string;
  UpdateIndexation: number | null;
  UpdateNSR: number | null;
  UpdateCost: number | null;
  UpdatePublishdtm: string;
  file: File | undefined;
  UpdateCostId?: string | null;
}

interface RejectNewCostRequest {
  Action: string;
  SkuId: string | undefined;
  Message: string | undefined;
}

export const approveNewCost = createAsyncThunk<any, ApproveNewCostRequest>(
  "CTDBBMOCostUpdateSlice/approveNewCost",
  async (payload, thunkApi) => {
    try {
      const formData = new FormData();
      formData.append("Action", payload.Action);
      formData.append("SkuId", payload.SkuId);
      formData.append(
        "UpdateCost",
        payload.UpdateCost != null ? payload.UpdateCost.toString() : "",
      );
      formData.append(
        "UpdateNSR",
        payload.UpdateNSR != null ? payload.UpdateNSR.toString() : "",
      );
      formData.append(
        "UpdateIndexation",
        payload.UpdateIndexation != null
          ? payload.UpdateIndexation.toString()
          : "",
      );
      formData.append("UpdatePublishdtm", payload.UpdatePublishdtm);
      if (payload.file) {
        formData.append("file", payload.file);
        formData.append("UpdateCostId", payload.UpdateCostId || "");
      }

      const requestPayload: any = formData;
      const config: any = {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      };
      const response = await httpClient.post(
        "/bmo/product/sku",
        requestPayload,
        config,
      );

      if (
        (response.status !== 200 && response.status !== 201) ||
        response.data.Status !== "success"
      ) {
        return thunkApi.rejectWithValue(
          response?.data?.Message || "Failed to create artifact",
        );
      }
      return response.data;
    } catch (error: any) {
      const status = error?.response?.status ?? error?.status;
      const errorData = error?.response?.data ?? error?.data;
      const errorMessage =
        errorData?.Message ||
        errorData?.message ||
        "Unexpected error occurred while approving cost update request.";
      return thunkApi.rejectWithValue({
        status,
        message:
          status === 400
            ? errorMessage
            : "Unexpected error occurred while approving cost update request.",
      });
    }
  },
);

export const rejectNewCost = createAsyncThunk<any, RejectNewCostRequest>(
  "CTDBBMOCostUpdateSlice/rejectNewCost",
  async (payload, thunkApi) => {
    try {
      const response = await httpClient.post("/bmo/product/sku", payload);
      const successStatusCodes = [200, 201, 202];
      if (
        !successStatusCodes.includes(response.status) ||
        response.data.Status !== "success"
      ) {
        if (response.data.Status == "error") {
          return thunkApi.rejectWithValue({
            message:
              "Unexpected error occurred while rejecting cost update request.",
          });
        } else {
          return thunkApi.rejectWithValue({
            message:
              "Unexpected error occurred while rejecting cost update request.",
          });
        }
      }
      return response.data;
    } catch (error: any) {
      if (isGatewayInvalidInputError(error)) {
        return thunkApi.rejectWithValue({
          message:
            "Invalid input detected. Please review the input text and try again!",
        });
      }
      return thunkApi.rejectWithValue({
        message:
          error?.response?.data?.Message ||
          error?.message ||
          "Unexpected error occurred while rejecting cost update request.",
      });
    }
  },
);

const CTDBBMOCostUpdateSlice = createSlice({
  name: "CTDBBMOCostUpdateSlice",
  initialState,
  reducers: {
    resetCostUpdateState: (state) => {
      state.isLoading = false;
      state.data = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(approveNewCost.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.data = null;
      })
      .addCase(
        approveNewCost.fulfilled,
        (state, action: PayloadAction<any>) => {
          if (action.payload.Status == "success") {
            state.isLoading = false;
            state.data = action.payload;
            state.error = null;
          } else if (action.payload.Status == "error") {
            state.isLoading = false;
            state.data = null;
            state.error = action.payload.message;
          }
        },
      )
      .addCase(approveNewCost.rejected, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.data = null;
        state.error = action.payload.message;
      });
  },
});

export const { resetCostUpdateState } = CTDBBMOCostUpdateSlice.actions;

export default CTDBBMOCostUpdateSlice.reducer;
